"use client";

/**
 * Global Error Page
 *
 * Next.js global-error.tsx pattern for handling errors at the root layout level.
 * This is the last line of defense for error handling - it catches errors that
 * occur in the root layout and provides a minimal error UI.
 *
 * IMPORTANT: This file MUST export a "use client" directive because:
 * 1. It's rendered when the root layout fails
 * 2. It needs to use client-side React features (useEffect)
 * 3. It must work even if the layout.tsx fails to render
 *
 * Features:
 * - Root layout error boundary (catches errors in layout.tsx)
 * - Minimal HTML structure (in case CSS fails to load)
 * - Global error handling with Sentry integration
 * - Refresh functionality to recover
 * - Uses class-based styling to stay CSP-compatible
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/global-error
 */

import { useEffect } from "react";

/**
 * Global Error Page Props Interface
 */
interface GlobalErrorProps {
  /** The error that was thrown */
  error: Error & { digest?: string };
  /** Function to reset and retry rendering */
  reset: () => void;
}

/**
 * Global Error Page Component
 *
 * This component is rendered when an error occurs in the root layout.
 * It uses minimal HTML structure since the layout (including global styles)
 * may have failed to render.
 */
export default function GlobalErrorPage({ error, reset }: GlobalErrorProps) {
  /**
   * Log error details and report to Sentry on mount
   */
  useEffect(() => {
    // Log to console for debugging
    console.error("Global error caught by global-error.tsx:", error);

    // Log error digest if available (Next.js specific)
    if (error.digest) {
      console.error("Error digest:", error.digest);
    }

    // Report to Sentry
    // Note: We use dynamic import to avoid issues if Sentry fails to load
    const reportError = async () => {
      try {
        const Sentry = await import("@sentry/nextjs");
        Sentry.captureException(error, {
          tags: {
            component: "GlobalErrorPage",
            level: "fatal",
          },
          extra: {
            digest: error.digest,
            isGlobalError: true,
          },
        });
      } catch (sentryError) {
        // If Sentry fails, at least log to console
        console.error("Failed to report to Sentry:", sentryError);
      }
    };

    reportError();
  }, [error]);

  const isDevelopment = process.env.NODE_ENV === "development";

  return (
    <html lang="en">
      <head>
        <title>Application Error - TrueDemand Holmes</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </head>
      <body className="min-h-screen bg-slate-50 p-6 text-slate-800 antialiased dark:bg-slate-900 dark:text-slate-100">
        <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-2xl items-center justify-center">
          <div className="w-full max-w-lg rounded-xl bg-white p-8 text-center shadow-lg dark:bg-slate-800">
            {/* Error Icon */}
            <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
              <svg
                className="size-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Error Title */}
            <h1 className="mb-2 text-2xl font-bold text-inherit">
              Critical Error
            </h1>

            {/* Error Description */}
            <p className="mb-6 text-slate-500 dark:text-slate-300">
              A critical error occurred that prevented the application from
              loading. Please refresh the page to try again.
            </p>

            {/* Development Mode: Show Error Details */}
            {isDevelopment && (
              <div className="mb-6 overflow-auto rounded-lg border border-red-200 bg-red-50 p-4 text-left dark:border-red-500/30 dark:bg-red-900/20">
                <p className="mb-2 font-mono text-sm text-red-500">
                  <strong>Error:</strong> {error.name}: {error.message}
                </p>
                {error.digest && (
                  <p className="mb-2 font-mono text-xs text-red-600/90 dark:text-red-300/90">
                    <strong>Digest:</strong> {error.digest}
                  </p>
                )}
                {error.stack && (
                  <pre className="whitespace-pre-wrap break-all font-mono text-xs text-red-600/90 dark:text-red-300/90">
                    {error.stack.split("\n").slice(0, 5).join("\n")}
                  </pre>
                )}
              </div>
            )}

            {/* Refresh Button */}
            <button
              onClick={reset}
              className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-6 py-3 text-base font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300"
              type="button"
            >
              <svg
                className="mr-2 size-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh Page
            </button>

            {/* Support Information */}
            <div className="mt-6 border-t border-slate-200 pt-6 dark:border-slate-700">
              <p className="text-sm text-slate-500 dark:text-slate-300">
                If the problem persists, please contact{" "}
                <a
                  href="mailto:support@truedemand.ai"
                  className="text-slate-900 underline dark:text-slate-100"
                >
                  support@truedemand.ai
                </a>
              </p>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
